// Step 2: Define Performance - My Plot with DCC-based Requirements
// Full implementation matching MyPlotView.jsx with 4-quadrant visualization
// Layout: Requirements Panel (Left) | Plot (Center) | Settings Drawer (Right)

import { useEffect, useMemo, useState } from 'preact/hooks'
import { MY_PLOT } from '../../data/definitions.js'
import { getBatchCurves } from '../../modules/dataService.js'
import {
    fundamentalsCells,
    fundamentalsCellsWithDcc,
    performanceCellsMeetingRequirements,
    workflowDccDataLoading,
    workflowDccDataProgress,
    workflowDccEvaluationComplete,
    workflowDccEvaluatedCellCount,
    workflowFullCellsCache,
    workflowRequiredEnergy,
    workflowRequiredPower
} from '../../state/benchmarkingWorkflowState.js'
import {
    allCells,
    basis,
    domain
} from '../../state/globalSignals.js'
import { getWatermarkGraphic } from '../../utils/chartWatermark.js'
import { calculateRequirements } from '../../utils/requirementCalculator.js'
import { IconInfo, IconRefresh, IconTarget } from '../shared/Icons.jsx'
import EChartsComponent from '../visualizations/EChartsComponent.jsx'

// Track previous basis/domain for conversion
let previousBasis = null
let previousDomain = null

/**
 * Round axis max to a nice number for display
 * Only adds minimal margin (5%) and rounds to nice values
 */
function roundAxisMax(value) {
  const expanded = value * 1.05 // 5% margin
  
  // Determine appropriate rounding step based on magnitude
  // Use smaller steps to avoid over-rounding
  let step
  if (expanded < 5) {
    step = 0.5 // Round to nearest 0.5 for very small values
  } else if (expanded < 20) {
    step = 1 // Round to nearest 1
  } else if (expanded < 100) {
    step = 5 // Round to nearest 5
  } else if (expanded < 500) {
    step = 10 // Round to nearest 10
  } else if (expanded < 2000) {
    step = 50 // Round to nearest 50
  } else if (expanded < 10000) {
    step = 100 // Round to nearest 100
  } else {
    step = 500 // Round to nearest 500
  }
  
  return Math.ceil(expanded / step) * step
}

/**
 * Calculate slider limits from DCC curves at 25°C
 *
 * MATLAB Reference:
 *   P_slider_min = max(0, min(cellfun(@(x) min(x.electrical.P_DCC(x.electrical.T_amb_DCC==298.15) / x.mechanical.m_cell), IDS)));
 *   P_slider_max = max(0, max(cellfun(@(x) max(x.electrical.P_DCC(x.electrical.T_amb_DCC==298.15) / x.mechanical.m_cell), IDS)));
 *   W_slider_min = max(0, min(cellfun(@(x) min(x.electrical.W_DCC(x.electrical.T_amb_DCC==298.15) / x.mechanical.m_cell), IDS)));
 *   W_slider_max = max(0, max(cellfun(@(x) max(x.electrical.W_DCC(x.electrical.T_amb_DCC==298.15) / x.mechanical.m_cell), IDS)));
 *
 * @param {Object[]} cells - Cells WITH DCC arrays (from fundamentalsCellsWithDcc)
 * @returns {{ xMin, xMax, yMin, yMax } | null} DCC-based limits, or null if no valid data
 */
function calculateDccSliderLimits(cells, basisValue, domainValue) {
  const cellXMins = []
  const cellXMaxs = []
  const cellYMins = []
  const cellYMaxs = []

  const T_target = 25   // °C (dataService converts 298.15K → 25°C)
  const T_tolerance = 0.5

  for (const cell of cells) {
    const T_amb = cell.electrical_T_amb_DCC || cell.electrical?.T_amb_DCC || []

    let X_DCC, Y_DCC
    if (domainValue === 'capacity-current') {
      X_DCC = cell.electrical_I_DCC || cell.electrical?.I_DCC || []
      Y_DCC = cell.electrical_C_DCC || cell.electrical?.C_DCC || []
    } else {
      X_DCC = cell.electrical_P_DCC || cell.electrical?.P_DCC || []
      Y_DCC = cell.electrical_W_DCC || cell.electrical?.W_DCC || []
    }

    if (T_amb.length === 0 || X_DCC.length === 0 || Y_DCC.length === 0) continue

    // Filter for 25°C indices
    const indices = []
    for (let i = 0; i < T_amb.length; i++) {
      if (Math.abs(T_amb[i] - T_target) < T_tolerance) indices.push(i)
    }
    if (indices.length < 2) continue

    let xVals = indices.map(i => X_DCC[i]).filter(v => typeof v === 'number' && !isNaN(v) && isFinite(v))
    let yVals = indices.map(i => Y_DCC[i]).filter(v => typeof v === 'number' && !isNaN(v) && isFinite(v))

    // Normalize by mass or volume (MATLAB: / x.mechanical.m_cell)
    if (basisValue === 'grav') {
      const mass_g = cell.mechanical?.mass_g || (cell.mechanical?.m_cell ? cell.mechanical.m_cell * 1000 : (cell.mass_g || 0))
      const m_kg = mass_g / 1000
      if (m_kg === 0) continue
      xVals = xVals.map(v => v / m_kg)
      yVals = yVals.map(v => v / m_kg)
    } else if (basisValue === 'vol') {
      const vol_cm3 = cell.mechanical?.vol_cell_cm3 || (cell.mechanical?.vol_cell ? cell.mechanical.vol_cell * 1e6 : (cell.vol_cell_cm3 || 0))
      const vol_L = vol_cm3 / 1000
      if (vol_L === 0) continue
      xVals = xVals.map(v => v / vol_L)
      yVals = yVals.map(v => v / vol_L)
    }

    if (xVals.length === 0 || yVals.length === 0) continue

    // MATLAB: min/max within this cell's DCC curve at 25°C
    cellXMins.push(Math.min(...xVals))
    cellXMaxs.push(Math.max(...xVals))
    cellYMins.push(Math.min(...yVals))
    cellYMaxs.push(Math.max(...yVals))
  }

  if (cellXMins.length === 0) return null

  return {
    xMin: Math.max(0, Math.min(...cellXMins)),
    xMax: Math.max(0, Math.max(...cellXMaxs)),
    yMin: Math.max(0, Math.min(...cellYMins)),
    yMax: Math.max(0, Math.max(...cellYMaxs)),
  }
}

/**
 * Scalar fallback for slider limits (used while DCC data loads)
 * Uses pre-computed peak values from the explorer endpoint.
 */
function calculateScalarSliderLimits(cells, basisValue, domainValue) {
  const xValues = []
  const yValues = []

  cells.forEach(cell => {
    let x, y

    if (domainValue === 'capacity-current') {
      if (basisValue === 'abs') {
        x = cell.electrical?.I_peak_A
        y = cell.electrical?.C_c10_Ah
      } else if (basisValue === 'grav') {
        x = cell.electrical?.I_grav_A_per_kg
        y = cell.electrical?.C_grav_Ah_per_kg
      } else if (basisValue === 'vol') {
        x = cell.electrical?.I_vol_A_per_L
        y = cell.electrical?.C_vol_Ah_per_L
      }
    } else {
      if (basisValue === 'abs') {
        x = cell.electrical?.P_peak_W
        y = cell.electrical?.E_c10_Wh
      } else if (basisValue === 'grav') {
        x = cell.electrical?.P_grav_W_per_kg
        y = cell.electrical?.E_grav_Wh_per_kg
      } else if (basisValue === 'vol') {
        x = cell.electrical?.P_vol_W_per_L
        y = cell.electrical?.E_vol_Wh_per_L
      }
    }

    if (x != null && !isNaN(x) && x > 0) xValues.push(x)
    if (y != null && !isNaN(y) && y > 0) yValues.push(y)
  })

  if (xValues.length === 0 || yValues.length === 0) {
    return { xMin: 0, xMax: 100, yMin: 0, yMax: 100 }
  }

  return {
    xMin: Math.max(0, Math.min(...xValues)),
    xMax: Math.max(...xValues),
    yMin: Math.max(0, Math.min(...yValues)),
    yMax: Math.max(...yValues)
  }
}

export default function Step2Performance() {
  // Use cells with full DCC data (loaded lazily)
  const allCellsData = fundamentalsCellsWithDcc.value || []
  const minimalCells = fundamentalsCells.value || []
  const loading = minimalCells.length === 0
  const dccLoading = workflowDccDataLoading.value
  const dccProgress = workflowDccDataProgress.value
  
  // Flipcard state for info overlay
  const [isFlipped, setIsFlipped] = useState(false)
  
  // Lazy load full cell data with DCC arrays when Step 2 is entered
  useEffect(() => {
    if (minimalCells.length === 0) return
    
    // Get cell IDs that need full data
    const cellIdsToLoad = minimalCells
      .map(cell => cell.id || cell.product_id)
      .filter(id => id && !workflowFullCellsCache.value.has(id))
    
    if (cellIdsToLoad.length === 0) {
      console.log('[Step2] All cells already have DCC data cached')
      return
    }
    
    console.log(`[Step2] Loading full DCC data for ${cellIdsToLoad.length} cells...`)
    workflowDccDataLoading.value = true
    workflowDccDataProgress.value = 0
    
    async function loadDccData() {
      try {
        const startTime = Date.now()

        // Load cells in batches of 10 (batch-curves endpoint max) with progress
        const batchSize = 10
        const newCache = new Map(workflowFullCellsCache.value)

        for (let i = 0; i < cellIdsToLoad.length; i += batchSize) {
          const batch = cellIdsToLoad.slice(i, i + batchSize)

          try {
            const result = await getBatchCurves(batch, ['dcc'])

            // Merge DCC curve data into cache with both nested and flat access patterns
            for (const cellId of batch) {
              const curveData = result.cells?.[cellId]?.dcc || {}
              const meta = result.metadata?.[cellId] || {}
              const existing = newCache.get(cellId) || {}
              // Look up original explorer cell data for scalar fields (format, KPIs, etc.)
              const explorerCell = (allCells.value || []).find(c => c.id === cellId || c.product_id === cellId) || {}
              newCache.set(cellId, {
                ...explorerCell,
                ...existing,
                id: cellId,
                manufacturer: meta.manufacturer || existing.manufacturer || explorerCell.manufacturer,
                name: meta.name || existing.name || explorerCell.name,
                mechanical: {
                  ...(explorerCell.mechanical || {}),
                  ...(existing.mechanical || {}),
                  m_cell: meta.m_cell ?? existing.mechanical?.m_cell ?? explorerCell.mechanical?.m_cell,
                  vol_cell: meta.vol_cell ?? existing.mechanical?.vol_cell ?? explorerCell.mechanical?.vol_cell
                },
                electrical: {
                  ...(explorerCell.electrical || {}),
                  ...(existing.electrical || {}),
                  ...curveData
                },
                // Flat access aliases (calculateDccSliderLimits uses cell.electrical_T_amb_DCC)
                electrical_T_amb_DCC: curveData.T_amb_DCC,
                electrical_P_DCC: curveData.P_DCC,
                electrical_W_DCC: curveData.W_DCC,
                electrical_I_DCC: curveData.I_DCC,
                electrical_C_DCC: curveData.C_DCC,
                electrical_t_max_DCC: curveData.t_max_DCC,
                electrical_eta_DCC: curveData.eta_DCC
              })
            }

            // Update progress
            const progress = Math.round(((i + batch.length) / cellIdsToLoad.length) * 100)
            workflowDccDataProgress.value = progress

          } catch (batchErr) {
            console.warn(`[Step2] Batch load error for cells ${i}-${i + batchSize}:`, batchErr)
          }
        }

        // Update cache signal (triggers recompute of fundamentalsCellsWithDcc)
        workflowFullCellsCache.value = newCache

        const duration = Date.now() - startTime
        console.log(`[Step2] ✅ Loaded DCC data for ${newCache.size} cells in ${duration}ms`)

      } catch (err) {
        console.error('[Step2] Failed to load DCC data:', err)
      } finally {
        workflowDccDataLoading.value = false
        workflowDccDataProgress.value = 100
      }
    }
    
    loadDccData()
  }, [minimalCells.length])
  
  // Calculate slider limits (MATLAB: P_slider_min/max, W_slider_min/max)
  // Prefer DCC-based limits once DCC data is loaded; scalar peaks as fallback while loading
  const sliderLimits = useMemo(() => {
    if (allCellsData.length > 0) {
      const dccLimits = calculateDccSliderLimits(allCellsData, basis.value, domain.value)
      if (dccLimits) return dccLimits
    }
    return calculateScalarSliderLimits(minimalCells, basis.value, domain.value)
  }, [allCellsData.length, minimalCells.length, basis.value, domain.value])
  
  // NOTE: Requirements initialization and basis/domain change handling is now done
  // synchronously in the requirementResults useMemo to avoid race conditions.
  // The old useEffect hooks have been removed.
  
  // Calculate DCC-based requirements
  // Requirements initialization uses sliderLimits (always available via scalar fallback)
  // DCC calculation only runs when allCellsData is available
  const requirementResults = useMemo(() => {
    console.log(`[DEBUG requirementResults] START - basis=${basis.value}, domain=${domain.value}`)
    console.log(`[DEBUG requirementResults] allCellsData: ${allCellsData.length}, minimalCells: ${minimalCells.length}`)

    // 1. Initialize requirements from sliderLimits (always available, scalar fallback)
    //    This MUST run even when DCC data is not yet loaded, to exit "Initializing requirements..."
    let power = workflowRequiredPower.value
    let energy = workflowRequiredEnergy.value

    const basisChanged = previousBasis !== null && previousBasis !== basis.value
    const domainChanged = previousDomain !== null && previousDomain !== domain.value

    if (power === null || energy === null || basisChanged || domainChanged) {
      // MATLAB: my_P_grav = (P_slider_min + P_slider_max) * 1/5
      // sliderLimits already has scalar fallback, so this always produces valid values
      power = Math.round((sliderLimits.xMin + sliderLimits.xMax) * 0.2)
      energy = Math.round((sliderLimits.yMin + sliderLimits.yMax) * 0.2)

      // Update the signals (this will trigger a re-render but with correct values)
      workflowRequiredPower.value = power
      workflowRequiredEnergy.value = energy
      previousBasis = basis.value
      previousDomain = domain.value
    }

    // 2. Only calculate DCC results if DCC data is available
    if (allCellsData.length === 0) {
      console.log(`[DEBUG requirementResults] SKIP DCC calc - waiting for DCC data (allCellsData empty)`)
      return []
    }

    console.log(`[DEBUG requirementResults] Using Power=${power}, Energy=${energy}`)

    // Calculate DCC interpolation
    const { results: dccResults, stats: dccStats } = calculateRequirements(
      allCellsData,
      power,
      energy,
      basis.value,
      domain.value
    )

    const matching = dccResults.filter(r => r.meets_requirements).length
    console.log(`[DEBUG requirementResults] ✅ RESULT: ${dccResults.length} cells with valid DCC data, ${matching} meet requirements`)
    if (dccStats.usedFallbackTemperature > 0) {
      console.log(`[DEBUG requirementResults] ⚠️ Used fallback temperature for ${dccStats.usedFallbackTemperature} cells`)
    }

    // Log first few cells for debugging
    if (dccResults.length > 0) {
      const sample = dccResults.slice(0, 3).map(r => ({
        name: `${r.cell.manufacturer} ${r.cell.name}`,
        power: r.achievable_power?.toFixed(1),
        energy: r.achievable_energy?.toFixed(1),
        meets: r.meets_requirements
      }))
      console.log(`[DEBUG requirementResults] Sample cells:`, sample)
    }

    return dccResults
  }, [allCellsData.length, minimalCells.length, sliderLimits, workflowRequiredPower.value, workflowRequiredEnergy.value, basis.value, domain.value])
  
  // Prepare chart data
  const chartData = useMemo(() => {
    const data = requirementResults.map(result => {
      const cell = result.cell
      const isMatching = result.meets_requirements
      
      return {
        value: [result.achievable_power, result.achievable_energy],
        name: `${cell.manufacturer || 'Unknown'} ${cell.name || 'Unknown'}`,
        cell: cell,
        isMatching: isMatching
      }
    })
    
    // Debug log chart data values to verify points are being calculated
    if (data.length > 0 && data.length <= 10) {
      console.log(`[Step2] Chart data points (${data.length}):`, data.map(d => `${d.name}: (${d.value[0]?.toFixed(1)}, ${d.value[1]?.toFixed(1)}) ${d.isMatching ? '✓' : '✗'}`).join(', '))
    }
    
    return data
  }, [requirementResults])
  
  const matchingCount = requirementResults.filter(r => r.meets_requirements).length
  
  // Update performanceCellsMeetingRequirements signal for workflow navigation
  useEffect(() => {
    const meetingCells = requirementResults
      .filter(r => r.meets_requirements)
      .map(r => r.cell)
    performanceCellsMeetingRequirements.value = meetingCells
  }, [requirementResults])

  // Track DCC evaluation completion for navigation (allows Step 3 even with no DCC results)
  useEffect(() => {
    // Mark evaluation as complete when:
    // - DCC data loaded and processed (allCellsData > 0, not loading)
    // - OR no DCC data available at all (minimalCells exist, not loading, allCellsData empty)
    if (!dccLoading && minimalCells.length > 0) {
      workflowDccEvaluationComplete.value = true
      workflowDccEvaluatedCellCount.value = requirementResults.length
    }
  }, [requirementResults, allCellsData.length, minimalCells.length, dccLoading])
  
  // Get units and labels
  const getUnits = () => {
    if (domain.value === 'capacity-current') {
      if (basis.value === 'grav') return { power: 'A/kg', energy: 'Ah/kg' }
      if (basis.value === 'vol') return { power: 'A/L', energy: 'Ah/L' }
      return { power: 'A', energy: 'Ah' }
    } else {
      if (basis.value === 'grav') return { power: 'W/kg', energy: 'Wh/kg' }
      if (basis.value === 'vol') return { power: 'W/L', energy: 'Wh/L' }
      return { power: 'W', energy: 'Wh' }
    }
  }
  
  const units = getUnits()
  
  const getAxisLabels = () => {
    if (domain.value === 'capacity-current') {
      return { x: 'Current', y: 'Capacity' }
    } else {
      return { x: 'Power', y: 'Energy' }
    }
  }
  
  const axisLabels = getAxisLabels()
  
  // Calculate rounded axis maxima (used for both axes and overlays)
  // IMPORTANT: Must include DCC interpolation results to ensure all data points are visible!
  // sliderLimits uses peak scalar values, but chartData uses DCC interpolated values
  const actualAxisRanges = useMemo(() => {
    // Start with slider limits (peak values)
    let xMax = sliderLimits.xMax
    let yMax = sliderLimits.yMax
    
    // Include all chart data points in the range calculation
    chartData.forEach(point => {
      if (point.value[0] != null && !isNaN(point.value[0])) {
        xMax = Math.max(xMax, point.value[0])
      }
      if (point.value[1] != null && !isNaN(point.value[1])) {
        yMax = Math.max(yMax, point.value[1])
      }
    })
    
    // Also ensure requirement lines are visible
    if (workflowRequiredPower.value != null) {
      xMax = Math.max(xMax, workflowRequiredPower.value * 1.1) // 10% margin for visibility
    }
    if (workflowRequiredEnergy.value != null) {
      yMax = Math.max(yMax, workflowRequiredEnergy.value * 1.1) // 10% margin for visibility
    }
    
    // Debug: show axis range expansion when data points extend beyond slider limits
    if (xMax > sliderLimits.xMax || yMax > sliderLimits.yMax) {
      console.log(`[Step2] Axis range expanded: slider(${sliderLimits.xMax?.toFixed(0)}, ${sliderLimits.yMax?.toFixed(0)}) -> actual(${xMax.toFixed(0)}, ${yMax.toFixed(0)}) to fit ${chartData.length} data points`)
    }
    
    return { xMax, yMax }
  }, [sliderLimits, chartData, workflowRequiredPower.value, workflowRequiredEnergy.value])
  
  const axisXMax = roundAxisMax(actualAxisRanges.xMax)
  const axisYMax = roundAxisMax(actualAxisRanges.yMax)
  
  // ECharts option with 4-quadrant visualization
  const chartOption = {
    graphic: [getWatermarkGraphic({ 
      position: 'top-right', 
      opacity: 0.12, 
      width: 236, 
      height: 60,
      grid: { left: 80, right: 40, top: 40, bottom: 80 }
    })],
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(20, 20, 20, 0.95)',
      borderColor: '#334155',
      borderWidth: 1,
      textStyle: { color: '#e2e8f0' },
      formatter: (params) => {
        const isMatch = params.data.isMatching
        const matchIcon = isMatch ? '✓' : '✗'
        const matchColor = isMatch ? '#4ade80' : '#f87171'
        
        return `
          <div style="font-family:'Big John',sans-serif;font-weight:600;margin-bottom:8px;color:#ea5b0c;text-transform:uppercase;font-size:11px;">${params.name}</div>
          <div style="margin-bottom:6px;padding-bottom:6px;border-bottom:1px solid rgba(255,255,255,0.1);color:${matchColor};font-family:'Roboto',sans-serif;">
            ${matchIcon} ${isMatch ? 'Meets requirements' : 'Does not meet requirements'}
          </div>
          <div style="color:#94a3b8;font-size:11px;margin-bottom:4px;font-family:'Roboto',sans-serif;">At 25°C from DCC curves:</div>
          <div style="margin:4px 0;font-family:'Roboto',sans-serif;">
            <span style="color:#94a3b8;">X:</span> <span style="color:#fff;font-weight:500;">${params.value[0].toFixed(1)} ${units.power}</span>
          </div>
          <div style="color:#94a3b8;font-size:10px;margin-bottom:6px;margin-left:18px;font-family:'Roboto',sans-serif;">
            → ${axisLabels.x} achievable at required ${axisLabels.y} (${workflowRequiredEnergy.value} ${units.energy})
          </div>
          <div style="margin:4px 0;font-family:'Roboto',sans-serif;">
            <span style="color:#94a3b8;">Y:</span> <span style="color:#fff;font-weight:500;">${params.value[1].toFixed(1)} ${units.energy}</span>
          </div>
          <div style="color:#94a3b8;font-size:10px;margin-bottom:6px;margin-left:18px;font-family:'Roboto',sans-serif;">
            → ${axisLabels.y} achievable at required ${axisLabels.x} (${workflowRequiredPower.value} ${units.power})
          </div>
        `
      }
    },
    grid: {
      left: 80,
      right: 40,
      top: 40,
      bottom: 80,
      containLabel: false
    },
    xAxis: {
      type: 'value',
      name: `Achievable ${axisLabels.x} / ${units.power}`,
      nameLocation: 'middle',
      nameGap: 45,
      min: 0,
      max: axisXMax, // Smart rounding based on magnitude
      nameTextStyle: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 15,
        fontWeight: 500,
        color: '#e2e8f0'
      },
      axisLine: {
        lineStyle: { color: 'rgba(255, 255, 255, 0.15)', width: 1.5 }
      },
      axisTick: {
        lineStyle: { color: 'rgba(255, 255, 255, 0.1)' }
      },
      axisLabel: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 13,
        color: '#cbd5e1'
      },
      splitLine: {
        lineStyle: { color: 'rgba(255, 255, 255, 0.08)' }
      }
    },
    yAxis: {
      type: 'value',
      name: `Achievable ${axisLabels.y} / ${units.energy}`,
      nameLocation: 'middle',
      nameGap: 55,
      min: 0,
      max: axisYMax, // Smart rounding based on magnitude
      nameTextStyle: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 15,
        fontWeight: 500,
        color: '#e2e8f0'
      },
      axisLine: {
        lineStyle: { color: 'rgba(255, 255, 255, 0.15)', width: 1.5 }
      },
      axisTick: {
        lineStyle: { color: 'rgba(255, 255, 255, 0.1)' }
      },
      axisLabel: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: 13,
        color: '#cbd5e1'
      },
      splitLine: {
        lineStyle: { color: 'rgba(255, 255, 255, 0.08)' }
      }
    },
    series: [
      // Main scatter series
      {
        id: 'main-scatter',
        type: 'scatter',
        data: chartData,
        symbolSize: 14, // Increased from 10 for better visibility with few cells
        itemStyle: {
          color: (params) => params.data.isMatching ? '#4ade80' : '#64748b',
          opacity: 0.9, // Increased opacity
          borderWidth: 2,
          borderColor: 'rgba(255, 255, 255, 0.5)'
        },
        emphasis: {
          scale: 1.4,
          itemStyle: {
            opacity: 1,
            borderWidth: 2,
            borderColor: '#ea5b0c',
            shadowBlur: 12,
            shadowColor: 'rgba(234, 91, 12, 0.5)'
          }
        }
      },
      // 4-quadrant overlay with markLine and markArea
      {
        id: 'requirements-overlay',
        type: 'scatter',
        data: [],
        silent: true,
        markLine: {
          silent: true,
          symbol: 'none',
          animation: false,
          lineStyle: {
            color: '#ea5b0c',
            width: 2.5,
            type: [8, 4]
          },
          label: { show: false },
          data: [
            { xAxis: workflowRequiredPower.value },
            { yAxis: workflowRequiredEnergy.value }
          ]
        },
        markArea: {
          silent: true,
          animation: false,
          label: { show: false },
          data: [
            // Green: both requirements met (top-right)
            [
              { 
                coord: [workflowRequiredPower.value, workflowRequiredEnergy.value],
                itemStyle: { color: 'rgba(34, 197, 94, 0.18)' }
              },
              { coord: [axisXMax, axisYMax] }
            ],
            // Yellow: only power met (bottom-right)
            [
              { 
                coord: [workflowRequiredPower.value, 0],
                itemStyle: { color: 'rgba(234, 179, 8, 0.15)' }
              },
              { coord: [axisXMax, workflowRequiredEnergy.value] }
            ],
            // Yellow: only energy met (top-left)
            [
              { 
                coord: [0, workflowRequiredEnergy.value],
                itemStyle: { color: 'rgba(234, 179, 8, 0.15)' }
              },
              { coord: [workflowRequiredPower.value, axisYMax] }
            ],
            // Red: neither met (bottom-left)
            [
              { 
                coord: [0, 0],
                itemStyle: { color: 'rgba(239, 68, 68, 0.12)' }
              },
              { coord: [workflowRequiredPower.value, workflowRequiredEnergy.value] }
            ]
          ]
        }
      }
    ],
    dataZoom: [
      { type: 'inside', xAxisIndex: 0, minSpan: 100, maxSpan: 100 }, // Disable zoom (fixed axes)
      { type: 'inside', yAxisIndex: 0, minSpan: 100, maxSpan: 100 }  // Disable zoom (fixed axes)
    ]
  }
  
  // Show loading state for initial load or DCC data loading
  if (loading) {
    return (
      <div className="step-performance">
        <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #94a3b8;">
          <div style="text-align: center;">
            <div style="font-size: 14px; margin-bottom: 8px;">Loading cells...</div>
            <div style="font-size: 12px; opacity: 0.7;">Please complete Step 1 first</div>
          </div>
        </div>
      </div>
    )
  }
  
  // Show DCC loading progress
  if (dccLoading) {
    return (
      <div className="step-performance">
        <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #94a3b8;">
          <div style="text-align: center;">
            <div style="font-size: 14px; margin-bottom: 8px;">Loading DCC curve data...</div>
            <div style="font-size: 12px; opacity: 0.7; margin-bottom: 12px;">
              This enables advanced performance calculations
            </div>
            <div style="width: 200px; height: 6px; background: rgba(255,255,255,0.1); border-radius: 3px; overflow: hidden;">
              <div style={{
                width: `${dccProgress}%`, 
                height: '100%', 
                background: '#ea5b0c',
                transition: 'width 0.3s ease'
              }} />
            </div>
            <div style="font-size: 11px; opacity: 0.5; margin-top: 8px;">
              {dccProgress}% complete
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  if (workflowRequiredPower.value === null || workflowRequiredEnergy.value === null) {
    return (
      <div className="step-performance">
        <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #94a3b8;">
          <div style="text-align: center;">
            <div style="font-size: 14px; margin-bottom: 8px;">Initializing requirements...</div>
            <div style="font-size: 12px; opacity: 0.7;">Calculating initial values from cell data</div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div className="step-performance">
      <div className="step-performance-card">
        {/* Left Panel: Requirements Input */}
        <div className="performance-requirements-panel" data-tour="benchmarking-step2">
        <div className="requirements-header">
          <IconTarget size={20} />
          <h3>Performance Requirements</h3>
        </div>
        
        <div className="requirements-body">
          <div className="requirement-input-group">
            <label className="requirement-label">
              <span>
                {domain.value === 'capacity-current' ? 'Required Current' : 'Required Power'}
              </span>
              <span className="requirement-unit">
                {domain.value === 'capacity-current' 
                  ? (basis.value === 'grav' ? 'A/kg' : basis.value === 'vol' ? 'A/L' : 'A')
                  : (basis.value === 'grav' ? 'W/kg' : basis.value === 'vol' ? 'W/L' : 'W')
                }
              </span>
            </label>
            <div className="input-with-slider">
              <input
                type="number"
                className="requirement-input"
                value={workflowRequiredPower.value ?? ''}
                placeholder="Calculating..."
                onChange={(e) => {
                  const val = parseFloat(e.target.value)
                  // Clamp to slider limits
                  const clamped = Math.max(sliderLimits.xMin, Math.min(sliderLimits.xMax, isNaN(val) ? 0 : val))
                  workflowRequiredPower.value = clamped
                }}
                min={Math.floor(sliderLimits.xMin)}
                max={Math.ceil(sliderLimits.xMax)}
                step={Math.max(1, Math.round((sliderLimits.xMax - sliderLimits.xMin) / 100))}
                disabled={workflowRequiredPower.value === null}
              />
              <input
                type="range"
                className="req-range-slider"
                value={workflowRequiredPower.value ?? sliderLimits.xMin}
                onInput={(e) => {
                  const val = parseFloat(e.target.value)
                  workflowRequiredPower.value = isNaN(val) ? sliderLimits.xMin : val
                }}
                min={Math.floor(sliderLimits.xMin)}
                max={Math.ceil(sliderLimits.xMax)}
                step={Math.max(1, Math.round((sliderLimits.xMax - sliderLimits.xMin) / 100))}
                disabled={workflowRequiredPower.value === null}
              />
            </div>
          </div>
          
          <div className="requirement-input-group">
            <label className="requirement-label">
              <span>
                {domain.value === 'capacity-current' ? 'Required Capacity' : 'Required Energy'}
              </span>
              <span className="requirement-unit">
                {domain.value === 'capacity-current'
                  ? (basis.value === 'grav' ? 'Ah/kg' : basis.value === 'vol' ? 'Ah/L' : 'Ah')
                  : (basis.value === 'grav' ? 'Wh/kg' : basis.value === 'vol' ? 'Wh/L' : 'Wh')
                }
              </span>
            </label>
            <div className="input-with-slider">
              <input
                type="number"
                className="requirement-input"
                value={workflowRequiredEnergy.value ?? ''}
                placeholder="Calculating..."
                onChange={(e) => {
                  const val = parseFloat(e.target.value)
                  // Clamp to slider limits
                  const clamped = Math.max(sliderLimits.yMin, Math.min(sliderLimits.yMax, isNaN(val) ? 0 : val))
                  workflowRequiredEnergy.value = clamped
                }}
                min={Math.floor(sliderLimits.yMin)}
                max={Math.ceil(sliderLimits.yMax)}
                step={Math.max(1, Math.round((sliderLimits.yMax - sliderLimits.yMin) / 100))}
                disabled={workflowRequiredEnergy.value === null}
              />
              <input
                type="range"
                className="req-range-slider"
                value={workflowRequiredEnergy.value ?? sliderLimits.yMin}
                onInput={(e) => {
                  const val = parseFloat(e.target.value)
                  workflowRequiredEnergy.value = isNaN(val) ? sliderLimits.yMin : val
                }}
                min={Math.floor(sliderLimits.yMin)}
                max={Math.ceil(sliderLimits.yMax)}
                step={Math.max(1, Math.round((sliderLimits.yMax - sliderLimits.yMin) / 100))}
                disabled={workflowRequiredEnergy.value === null}
              />
            </div>
          </div>
          
          <button
            className="requirement-reset-btn"
            onClick={() => {
              // MATLAB: my_P_grav = (P_slider_min + P_slider_max) * 1/5
              workflowRequiredPower.value = Math.round((sliderLimits.xMin + sliderLimits.xMax) * 0.2)
              workflowRequiredEnergy.value = Math.round((sliderLimits.yMin + sliderLimits.yMax) * 0.2)
            }}
            disabled={workflowRequiredPower.value === null || workflowRequiredEnergy.value === null}
          >
            <IconRefresh size={14} />
            <span>Reset to Default</span>
          </button>
          
          <div className="requirement-summary">
            <div className="summary-label">Cells Meeting Requirements</div>
            <div className="summary-count">
              {chartData.length > 0 ? (
                <>
                  <span className="summary-number">{matchingCount}</span>
                  <span className="summary-total">/ {chartData.length}</span>
                </>
              ) : (
                <span className="summary-total">No DCC data at 25°C</span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Center: Main Plot Container with Flipcard */}
      <div className={`workflow-my-plot-flipcard ${isFlipped ? 'flipped' : ''}`}>
        {/* Front: Plot */}
        <div className="workflow-my-plot-container flipcard-front">
          <button className="flip-btn" onClick={() => setIsFlipped(true)} title="Show info">?</button>
          <div className="workflow-my-plot-header">
            <div className="workflow-my-plot-title">
              <IconTarget size={18} />
              <span>My Plot</span>
              <span className="workflow-my-plot-count">
                {chartData.length > 0
                  ? `${matchingCount} / ${chartData.length} Meet Requirements`
                  : 'No DCC data at 25°C'}
              </span>
            </div>
          </div>
          <div className="workflow-my-plot-main">
            {chartData.length === 0 && !dccLoading ? (
              <div className="dcc-empty-state">
                <div className="dcc-empty-state-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 9v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                  </svg>
                </div>
                <h4>No performance data available</h4>
                <p>
                  The selected cells don't have DCC curves at 25°C, which are needed
                  for the power/energy interpolation in this plot.
                </p>
                <p className="dcc-empty-state-hint">
                  You can skip this step — click <strong>Next</strong> to proceed
                  to Step 3 where all {minimalCells.length} cells will be available for comparison.
                </p>
              </div>
            ) : (
              <EChartsComponent
                option={chartOption}
                notMerge={false}
                replaceMerge={['series']}
              />
            )}
          </div>
        </div>
        
        {/* Back: Info */}
        <div className="workflow-my-plot-info flipcard-back">
          <button className="flip-btn flip-btn-back" onClick={() => setIsFlipped(false)} title="Close">×</button>
          <div className="workflow-my-plot-header">
            <div className="workflow-my-plot-title">
              <IconInfo size={18} />
              <span>{MY_PLOT.title}</span>
            </div>
          </div>
          <div className="workflow-my-plot-info-content">
            <p className="info-intro">{MY_PLOT.intro}</p>
            <p className="info-concept">{MY_PLOT.concept}</p>
            <div className="info-how-to">
              <h4>{MY_PLOT.howToUse.title}</h4>
              <ul>
                {MY_PLOT.howToUse.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      </div>
    </div>
  )
}
